import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import Validators from "../../../utils/Validators";
import ModalsContext from "../../../../domain/providers/modal/ModalsContext";
import ModalsContextType from "../../../../domain/providers/modal/ModalsContextType";
import CityContext from "../../../../domain/providers/city/CityContext";
import CityContextType from "../../../../domain/providers/city/CityContextType";
import CarEntity from "../../../../domain/entities/CarEntity";
import Button from "../Button";
import { routes } from "../../routes/RoutesComponent";
import { getUrlCar } from "../../../utils/Contants";
import "./PromoLeadModalStyles.scss";

import backgroundPattern from "./assets/background-pattern.png";
import linesTile from "./assets/lines-tile.png";
import heroShape from "./assets/hero-shape.svg";
import tagSale from "./assets/tag-sale.svg";
import confettiTriangles from "./assets/confetti-triangles.svg";
import wcarLogo from "./assets/wcar-logo.svg";
import carrosEnOfertaText from "./assets/carros-en-oferta-text.svg";
import roadStripe1 from "./assets/road-stripe-1.svg";
import roadStripe2 from "./assets/road-stripe-2.svg";
import carPhoto from "./assets/car-photo.png";
import carShadowShape from "./assets/car-shadow-shape.svg";
import iconMedal from "./assets/icon-medal.svg";

// El header y el footer son recortes fijos del diseño de Figma (node-id
// 1163:3550), escalados con container query units. El formulario del medio
// queda en flujo normal, con los inputs/botón reales del sitio a su tamaño
// natural -no escalados al canvas- para que sean usables.
const headerPx = (value: number) => `calc(${value} / 840 * 100cqw)`;
const footerPx = (value: number) => `calc(${value} / 840 * 100cqw)`;

type Inputs = {
    name: string;
    phone: string;
    email: string;
    cityId: string;
    financing: string;
    tradeIn: string;
    timeToBuy: string;
};

const STEPS: (keyof Inputs)[][] = [
    ["name", "phone", "email", "cityId"],
    ["financing", "tradeIn", "timeToBuy"],
];

const formatPhoneNumber = (phone: string): string => {
    const digits = phone.replace(/\D/g, "");
    if (digits.startsWith("57") && digits.length === 12) return `+${digits}`;
    if (digits.startsWith("3") && digits.length === 10) return `+57${digits}`;
    return phone;
};

const LEADS_WEBHOOK_URL =
    "https://script.google.com/macros/s/AKfycbxkhQx6fPo6dK0jbs18WBvpsrDAgstZxAG-fAP4d1u3PVH4FQtbSc1NsqfNXK57grX6/exec";

interface PromoLeadModalProps {
    car: CarEntity;
    closeModal: () => void;
}

export default function PromoLeadModal({ car, closeModal }: PromoLeadModalProps) {
    const { addToast } = useContext(ModalsContext) as ModalsContextType;
    const { cities } = useContext(CityContext) as CityContextType;
    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState(0);
    const {
        register,
        handleSubmit,
        trigger,
        formState: { errors, isValid },
    } = useForm<Inputs>({ mode: "onChange" });

    const isLastStep = step === STEPS.length - 1;

    const goNext = async () => {
        const valid = await trigger(STEPS[step]);
        if (valid) setStep((s) => Math.min(s + 1, STEPS.length - 1));
    };

    const goBack = () => setStep((s) => Math.max(s - 1, 0));

    const stockId = car.type_negotiation != null ? car.type_negotiation + car.id : car.id;
    const carLink = `${window.location.origin}${routes.detailedCar.relativePath}/${getUrlCar(car)}`;

    const onSubmit = handleSubmit(async (data) => {
        const city = cities.find((c) => c.id === data.cityId);

        setIsLoading(true);
        try {
            await fetch(LEADS_WEBHOOK_URL, {
                method: "POST",
                mode: "no-cors",
                headers: { "Content-Type": "text/plain" },
                body: JSON.stringify({
                    name: data.name,
                    phone: formatPhoneNumber(data.phone),
                    email: data.email,
                    city: city?.name ?? data.cityId,
                    carId: stockId,
                    carName: car.name,
                    financing: data.financing,
                    tradeIn: data.tradeIn,
                    timeToBuy: data.timeToBuy,
                    source: "Google",
                    page: carLink,
                    date: new Date().toISOString(),
                }),
            });
            window.localStorage.setItem("promo_ab_lead_sent", "true");
            addToast("¡Gracias! Ya tenemos tus datos, pronto te contactamos.", "success", undefined);
            closeModal();
        } catch (error) {
            console.error("Error al enviar el formulario de la promo:", error);
            addToast("Error al enviar el formulario. Intenta de nuevo.", "error", undefined);
        } finally {
            setIsLoading(false);
        }
    });

    return (
        <div className="promo_lead_modal_container">
            <div className="promo_lead_modal">
                <button type="button" className="promo_lead_modal__close" onClick={closeModal} aria-label="Cerrar">
                    ✕
                </button>

                <div className="promo_lead_modal__hero-header">
                    <img className="promo_lead_modal__layer" style={{ top: 0, left: 0, width: "100%", height: headerPx(360) }} src={backgroundPattern} alt="" aria-hidden="true" />
                    <img className="promo_lead_modal__layer" style={{ top: headerPx(39), left: 0, width: "100%", height: headerPx(413) }} src={heroShape} alt="" aria-hidden="true" />
                    <div
                        className="promo_lead_modal__lines"
                        style={{ left: headerPx(722), top: headerPx(62), width: headerPx(90), height: headerPx(90), backgroundImage: `url(${linesTile})` }}
                        aria-hidden="true"
                    />
                    <img className="promo_lead_modal__layer" style={{ left: headerPx(157.82), top: headerPx(32), width: headerPx(167.492), height: headerPx(145.664), transform: "rotate(-46deg)" }} src={tagSale} alt="" aria-hidden="true" />
                    <img className="promo_lead_modal__layer" style={{ left: headerPx(545.84), top: headerPx(62.67), width: headerPx(129.001), height: headerPx(130.142), transform: "rotate(15.24deg)" }} src={confettiTriangles} alt="" aria-hidden="true" />
                    <img className="promo_lead_modal__layer" style={{ left: headerPx(353.13), top: headerPx(36), width: headerPx(133.75), height: headerPx(40) }} src={wcarLogo} alt="wcar" />
                    <img className="promo_lead_modal__layer" style={{ left: headerPx(275), top: headerPx(153), width: headerPx(314), height: headerPx(90) }} src={carrosEnOfertaText} alt="Carros usados en oferta" />
                    <p className="promo_lead_modal__title" style={{ left: headerPx(299), top: headerPx(122), fontSize: headerPx(40) }}>
                        LOS MEJORES
                    </p>
                </div>

                <div className="promo_lead_modal__body">
                    <p className="promo_lead_modal__heading">
                        El carro usado que quieres <em>lo encuentras en wcar</em>
                    </p>
                    <p className="promo_lead_modal__subheading">
                        Carros usados seleccionados, con peritaje certificado, garantía de 6 meses y financiación
                        hasta el 100%.
                    </p>

                    <form onSubmit={onSubmit}>
                        <div className="promo_lead_modal__progress">
                            Paso {step + 1} de {STEPS.length}
                            <div className="promo_lead_modal__progress-bar">
                                <span style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
                            </div>
                        </div>

                        {step === 0 && (
                            <>
                                <div className="form-group my-3">
                                    <div className="group">
                                        <label className="mandatory">Nombre completo</label>
                                        <input
                                            className="form-control"
                                            type="text"
                                            placeholder="Nombre completo"
                                            {...register("name", Validators({ required: true }))}
                                        />
                                        <ErrorMessage as="aside" errors={errors} name="name" />
                                    </div>
                                    <div className="group mt-3 mt-md-0">
                                        <label className="mandatory">Celular</label>
                                        <input
                                            className="form-control"
                                            type="text"
                                            placeholder="Número de celular"
                                            {...register("phone", Validators({ required: true }))}
                                        />
                                        <ErrorMessage as="aside" errors={errors} name="phone" />
                                    </div>
                                </div>
                                <div className="form-group my-3">
                                    <div className="group">
                                        <label className="mandatory">Correo</label>
                                        <input
                                            className="form-control"
                                            type="email"
                                            placeholder="ejemplo@gmail.com"
                                            {...register("email", Validators({ required: true, email: true }))}
                                        />
                                        <ErrorMessage as="aside" errors={errors} name="email" />
                                    </div>
                                    <div className="group mt-3 mt-md-0">
                                        <label className="mandatory">Ciudad</label>
                                        <select className="form-select" {...register("cityId", Validators({ required: true }))}>
                                            <option value="">Selecciona una ciudad</option>
                                            {cities.map((city) => (
                                                <option key={city.id} value={city.id}>
                                                    {city.name}
                                                </option>
                                            ))}
                                        </select>
                                        <ErrorMessage as="aside" errors={errors} name="cityId" />
                                    </div>
                                </div>
                            </>
                        )}

                        {step === 1 && (
                            <>
                                <div className="promo_lead_modal__selected-car">
                                    Vehículo de interés: <strong>{car.name}</strong>
                                </div>
                                <div className="form-group my-3">
                                    <div className="group">
                                        <label className="mandatory">¿Necesitas financiación para tu compra?</label>
                                        <select className="form-select" {...register("financing", Validators({ required: true }))}>
                                            <option value="">Selecciona una opción</option>
                                            <option value="Sí">Sí</option>
                                            <option value="No">No</option>
                                            <option value="Aún no estoy seguro">Aún no estoy seguro</option>
                                        </select>
                                        <ErrorMessage as="aside" errors={errors} name="financing" />
                                    </div>
                                    <div className="group mt-3 mt-md-0">
                                        <label className="mandatory">¿Tienes un carro para dejar como parte de pago?</label>
                                        <select className="form-select" {...register("tradeIn", Validators({ required: true }))}>
                                            <option value="">Selecciona una opción</option>
                                            <option value="Sí">Sí</option>
                                            <option value="No">No</option>
                                        </select>
                                        <ErrorMessage as="aside" errors={errors} name="tradeIn" />
                                    </div>
                                </div>
                                <div className="form-group my-3">
                                    <div className="w-100">
                                        <label className="mandatory">¿En cuánto tiempo quieres comprar tu carro?</label>
                                        <select className="form-select" {...register("timeToBuy", Validators({ required: true }))}>
                                            <option value="">Selecciona una opción</option>
                                            <option value="De 0 a 1 mes">De 0 a 1 mes</option>
                                            <option value="De 1 a 3 meses">De 1 a 3 meses</option>
                                            <option value="Más de 3 meses">Más de 3 meses</option>
                                        </select>
                                        <ErrorMessage as="aside" errors={errors} name="timeToBuy" />
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="promo_lead_modal__submit">
                            {step > 0 && (
                                <Button type="button" variant="secondary" label="Atrás" onClick={goBack} />
                            )}
                            {!isLastStep ? (
                                <Button type="button" variant="cyan" label="Siguiente" onClick={goNext} />
                            ) : (
                                <Button
                                    type="submit"
                                    variant="cyan"
                                    label={isLoading ? "Enviando..." : "Enviar"}
                                    disabled={!isValid || isLoading}
                                />
                            )}
                        </div>
                    </form>
                </div>

                <div className="promo_lead_modal__hero-footer">
                    <img className="promo_lead_modal__layer" style={{ left: footerPx(-63.78), top: footerPx(49.58), width: footerPx(879.341), height: footerPx(152.409), transform: "rotate(-7.61deg)" }} src={roadStripe1} alt="" aria-hidden="true" />
                    <img className="promo_lead_modal__layer" style={{ left: footerPx(-97.78), top: footerPx(172.58), width: footerPx(879.341), height: footerPx(152.409), transform: "rotate(-7.61deg)" }} src={roadStripe2} alt="" aria-hidden="true" />
                    <img className="promo_lead_modal__layer" style={{ left: footerPx(170.996), top: footerPx(9.654), width: footerPx(685.044), height: footerPx(319.664), transform: "rotate(-7.03deg)" }} src={carPhoto} alt="Carro usado wcar" />
                    <img className="promo_lead_modal__layer" style={{ left: footerPx(150.353), top: footerPx(10.246), width: footerPx(245.318), height: footerPx(132.072), transform: "rotate(-7.03deg)" }} src={carShadowShape} alt="" aria-hidden="true" />
                    <div
                        className="promo_lead_modal__badge"
                        style={{ left: footerPx(200.91), top: footerPx(71.22), width: footerPx(200), height: footerPx(42), transform: "rotate(-7.71deg)" }}
                    >
                        <img src={iconMedal} width={28} height={28} alt="" aria-hidden="true" />
                        <span>Garantía</span>
                    </div>
                    <p className="promo_lead_modal__tyc" style={{ left: footerPx(667), top: footerPx(222), fontSize: footerPx(24) }}>
                        Aplican TyC
                    </p>
                </div>
            </div>
        </div>
    );
}
